<script setup lang="ts">
// Generic ERP entity selector: an inline type-ahead (NxAutocompleteInput) plus a
// minimal browse modal — an NxSearchList (search box over a list of id + the
// registry's display columns). No heavy grid. The "more" (…) button opens the
// modal.
//
// A drop-in replacement for a plain input — it renders NO caption/label of its
// own, so the host lays it out like any field. `labelDe` only names the modal.
//
// App-agnostic: German-default strings (no useI18n), only nx-ui tokens. The
// embedded NxAutocompleteInput needs the host to provide `i18nInjectionKey` +
// `autocomplete_messages.*` (every Formica shell already does).
import { computed, ref, watch } from 'vue';
import {
  NxAutocompleteInput,
  NxButton,
  NxModal,
  NxSearchList,
  type NxSearchListColumn,
} from '@blpde/nx-ui';
import { ErpEntityClient } from './client';
import { getErpEntity } from './registry';
import { useErpEntitySearch } from './useErpEntitySearch';
import type { ErpBaseFilterValue, ErpEntityRow } from './types';

const props = withDefaults(
  defineProps<{
    /** ERP resource key from the registry (e.g. 'products', 'customers'). */
    resource: string;
    /** Names the browse modal; defaults to the registry label. */
    labelDe?: string;
    placeholderDe?: string;
    modalTitleDe?: string;
    /**
     * Pre-filters AND-combined into every lookup (e.g. scope to a category).
     * A bare scalar filters by equality; `{ op, value }` picks the operator
     * (e.g. `{ op: 'in', value: 'draft,open' }` for a set of states).
     */
    baseFilter?: Map<string, ErpBaseFilterValue>;
    disabled?: boolean;
    /** Render ONLY the browse modal (no inline type-ahead); open it via `v-model:open`. */
    browseOnly?: boolean;
  }>(),
  {
    labelDe: undefined,
    placeholderDe: 'Suchen…',
    modalTitleDe: '',
    baseFilter: undefined,
    disabled: false,
    browseOnly: false,
  },
);

const idModel = defineModel<number | null>('id', { required: true });
const nameModel = defineModel<string | null>('name', { required: true });
// Modal visibility is a model so a host can open the browser programmatically
// (e.g. browse-only usage); the "…" button and the modal's close both flip it.
const open = defineModel<boolean>('open', { default: false });

const entity = computed(() => getErpEntity(props.resource));
const label = computed(() => props.labelDe ?? entity.value?.labelDe ?? props.resource);
const displayField = computed(() => entity.value?.displayField ?? 'name');
const searchFields = computed(() => entity.value?.searchFields ?? [displayField.value]);
const searchIgnoreId = computed(() => entity.value?.searchIgnoreId ?? false);
const listColumns = computed(
  () => entity.value?.gridColumns ?? [{ key: displayField.value, labelDe: label.value }],
);

// Same label shape as the type-ahead: join the search fields (e.g. "ART-1103 ·
// Mineralwasser") so the picked value is self-explanatory in the field.
function buildLabel(row: ErpEntityRow): string {
  const parts = searchFields.value.map((f) => row[f]).filter((v) => v != null && v !== '');
  return parts.length ? parts.map(String).join(' · ') : String(row.id);
}

// --- inline type-ahead ---
const { queryFn, data, isLoading, reset } = useErpEntitySearch(props.resource, () => props.baseFilter);
watch(
  () => JSON.stringify([...(props.baseFilter?.entries() ?? [])]),
  () => reset(),
);

// --- browse modal (minimal list) ---
const shadowId = ref<number | null>(null);
const shadowName = ref<string | null>(null);
const modalQuery = ref('');
const PER_PAGE = 25;
const listRows = ref<ErpEntityRow[]>([]);
const listLoading = ref(false);
// A "load more" keeps the rows on screen; only a fresh list shows the loading state.
const listAppending = ref(false);
const listCount = ref(0);
const listPage = ref(1);
const hasMore = computed(() => listRows.value.length < listCount.value);

const searchListColumns = computed<NxSearchListColumn<ErpEntityRow>[]>(() => [
  { key: 'id', label: 'ID', width: 'calc(var(--nxt-size-unit) * 3.5)', mono: true, muted: true },
  ...listColumns.value.map((c) => ({ key: c.key, label: c.labelDe })),
]);
const selectedRow = computed(() => listRows.value.find((row) => row.id === shadowId.value) ?? null);

const onlyColumns = computed(() => {
  const keys = new Set<string>(['id', displayField.value]);
  for (const c of listColumns.value) keys.add(c.key);
  for (const f of searchFields.value) keys.add(f);
  return [...keys];
});

let listSeq = 0;
async function fetchList(append: boolean): Promise<void> {
  const seq = ++listSeq;
  listLoading.value = true;
  listAppending.value = append;
  try {
    const query = modalQuery.value.trim();
    const res = await ErpEntityClient.list({
      resource: props.resource,
      only: onlyColumns.value,
      sortColumn: displayField.value,
      sortOrder: 'asc',
      perPage: PER_PAGE,
      page: listPage.value,
      baseFilter: props.baseFilter,
      search: query ? { columns: searchFields.value, query, ignoreId: searchIgnoreId.value } : undefined,
    });
    if (seq !== listSeq) return; // superseded by a newer fetch
    listRows.value = append ? [...listRows.value, ...res.rows] : res.rows;
    listCount.value = res.count;
  } catch (err) {
    if (seq === listSeq && !append) listRows.value = [];
    console.warn(`[ErpEntitySelect] list "${props.resource}" failed`, err);
  } finally {
    if (seq === listSeq) listLoading.value = false;
  }
}

let searchTimer: ReturnType<typeof setTimeout> | undefined;
watch(modalQuery, () => {
  if (!open.value) return;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    listPage.value = 1;
    void fetchList(false);
  }, 200);
});

function resetBrowse(): void {
  shadowId.value = idModel.value;
  shadowName.value = nameModel.value;
  modalQuery.value = '';
  listPage.value = 1;
  listRows.value = [];
  void fetchList(false);
}
function openModal(): void {
  open.value = true;
}
// Opening (via the "…" button or an external `v-model:open`) re-seeds + fetches.
// Clear the query on close so the next open starts from '' — otherwise
// resetBrowse setting modalQuery='' would re-trigger the modalQuery watch and
// fire a redundant debounced fetch on top of resetBrowse's own.
watch(open, (isOpen) => {
  if (isOpen) resetBrowse();
  else modalQuery.value = '';
});
function loadMore(): void {
  listPage.value += 1;
  void fetchList(true);
}
function pickRow(row: ErpEntityRow | null): void {
  if (!row) return;
  shadowId.value = row.id;
  shadowName.value = buildLabel(row);
}
function confirmSelection(): void {
  idModel.value = shadowId.value;
  nameModel.value = shadowName.value;
  open.value = false;
}
function pickRowAndConfirm(row: ErpEntityRow): void {
  pickRow(row);
  confirmSelection();
}
function clearSelection(): void {
  idModel.value = null;
  nameModel.value = null;
}
</script>

<template>
  <div class="erp-entity-select">
    <NxAutocompleteInput
      v-if="!browseOnly"
      v-model:id_value="idModel"
      v-model:name_value="nameModel"
      :options="data"
      :is-loading="isLoading"
      :placeholder="placeholderDe"
      :disabled="disabled"
      search-mode="contains"
      :search-ignore-id="searchIgnoreId"
      :on-more-button-click="openModal"
      :on-dismiss="clearSelection"
      @updated-search="queryFn"
    />

    <NxModal v-model:open="open" size="wide">
      <template #header>{{ modalTitleDe || `${label} auswählen` }}</template>
      <template #body>
        <div class="erp-entity-modal">
          <NxSearchList
            v-model:query="modalQuery"
            :selected="selectedRow"
            :rows="listRows"
            :columns="searchListColumns"
            :loading="listLoading && !listAppending"
            :placeholder="placeholderDe"
            :aria-label="label"
            empty-text="Keine Ergebnisse"
            loading-text="Wird geladen…"
            @update:selected="pickRow"
            @activate="pickRowAndConfirm"
          />
          <div v-if="hasMore" class="erp-entity-more">
            <NxButton
              variant="outline"
              :loading="listLoading && listAppending"
              :disabled="listLoading && !listAppending"
              @click="loadMore"
            >
              Mehr laden
            </NxButton>
          </div>
        </div>
      </template>
      <template #footer="{ close }">
        <NxButton :disabled="shadowId == null" @click="confirmSelection">Übernehmen</NxButton>
        <NxButton variant="outline" @click="close">Abbrechen</NxButton>
      </template>
    </NxModal>
  </div>
</template>

<style scoped>
.erp-entity-select {
  display: block;
  width: 100%;
}
.erp-entity-modal {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
}
.erp-entity-more {
  display: flex;
  justify-content: center;
}
</style>
